import type { PageDescriptor, ProjectDescriptor } from "@kaleidoscope-solutions/engine-wasm"
import type { Component } from "vue"
import type { RouteLocationRaw } from "vue-router"
import type { ModelFacade } from "@/utils/useEngineModel/ModelFacade"
import { MessageKind } from "@kaleidoscope-solutions/engine-wasm"
import { defineStore } from "pinia"
import messageHandler, { UIMessageKind } from "@/logic/message-handler"
import { useSettingsStore } from "@/stores"
import { slugify } from "@/utils"
import { ALPHA_PROJECT_DETAIL_PAGES } from "@/utils/alphaModel"

export interface Nav {
  name: string
  title: string
  icon?: Component
  expanded?: boolean
  pages?: Nav[]
  subPages?: Nav[]
  hideInSidenav?: boolean
  dataTest?: string
  slug: string
  theme?: string
}

interface State {
  model?: ModelFacade
  projects: ProjectDescriptor[]
  manageData?: ProjectDescriptor
  isQuantitySoldEmpty: boolean
}

export const useProjectStore = defineStore("project", {
  state: (): State => ({
    model: undefined,
    projects: [],
    manageData: undefined,
    isQuantitySoldEmpty: true,
  }),

  actions: {
    async initialise(model: ModelFacade) {
      this.model = model
      this.setupListeners()
      await this.retrieveProjects()
    },
    setupListeners() {
      // Check if quantity sold is empty after an import is complete to update
      // the dashboard.
      messageHandler.on(MessageKind.IntegrationImportCompleted, () => {
        messageHandler.emit(UIMessageKind.DataImported, undefined)
        this.retrieveProjects()
      })
    },
    async retrieveProjects() {
      this.projects = await this.modelApi.projects()

      try {
        this.manageData =
          (await this.modelApi.manageData()) as ProjectDescriptor | undefined
      } catch {

      }
    },
    retrieveProjectBySlug(projectSlug: string) {
      return this.allProjects.find(
        (project: ProjectDescriptor) =>
          slugify(project.name) === projectSlug
      )
    },
    getProjectFromPage(pageName: string) {
      const lowerPageName = pageName.toLowerCase()
      function pageMatches(page: PageDescriptor): boolean {
        if (page.name?.toLowerCase() === lowerPageName) {
          return true
        }
        if (Array.isArray(page.subPages)) {
          return page.subPages.some(pageMatches)
        }
        return false
      }
      return [...this.projects, ...(this.manageData ? [this.manageData] : [])].find(
        (project: ProjectDescriptor) =>
          project.pages.some(pageMatches)
      )
    },

    getPage(pageName: string) {
      const lowerPageName = pageName.toLowerCase()
      function findPage(page: PageDescriptor): PageDescriptor | undefined {
        if (page.name?.toLowerCase() === lowerPageName) {
          return page
        }
        if (Array.isArray(page.subPages)) {
          for (const subPage of page.subPages) {
            const found = findPage(subPage)
            if (found) {
              return found
            }
          }
        }
        return undefined
      }
      for (const project of [...this.projects, ...(this.manageData ? [this.manageData] : [])]) {
        for (const page of project.pages) {
          const found = findPage(page)
          if (found) {
            return found
          }
        }
      }
      return undefined
    },

    findTopLevelPageForSubPage(
      pages: PageDescriptor[],
      targetPageName: string
    ): { topLevelPageName: string; isSubPage: boolean } | undefined {
      const lowerTargetPageName = targetPageName.toLowerCase()

      function containsTarget(page: PageDescriptor): boolean {
        if (page.name?.toLowerCase() === lowerTargetPageName) {
          return true
        }

        if (!Array.isArray(page.subPages)) {
          return false
        }

        return page.subPages.some(containsTarget)
      }

      for (const page of pages) {
        if (page.name?.toLowerCase() === lowerTargetPageName) {
          return {
            topLevelPageName: page.name,
            isSubPage: false,
          }
        }

        if (Array.isArray(page.subPages) && page.subPages.some(containsTarget)) {
          return {
            topLevelPageName: page.name,
            isSubPage: true,
          }
        }
      }

      return undefined
    },

    getPageLink(pageName: string, sectionId?: string): RouteLocationRaw | null {
      const project = this.getProjectFromPage(pageName)

      if (!project) {
        console.error("Could not find project for ", pageName)
        return null
      }

      const pageLookup = this.findTopLevelPageForSubPage(project.pages, pageName)
      if (!pageLookup) {
        console.error("Could not find page for ", pageName)
        return null
      }

      const hash = sectionId
        ? (sectionId.startsWith("#") ? sectionId : `#${sectionId}`)
        : ""

      if (pageLookup.isSubPage) {
        return {
          name: "/projects/[project]/[page]/[subpage]",
          params: {
            project: slugify(project.name),
            page: slugify(pageLookup.topLevelPageName),
            subpage: slugify(pageName),
          },
          hash
        }
      }

      return {
        name: "/projects/[project]/[page]/",
        params: {
          project: slugify(project.name),
          page: slugify(pageName),
        },
        hash
      }
    }
  },
  getters: {
    modelApi(state) {
      if (state.model === undefined) {
        throw new Error("Model not initialised")
      }
      return state.model?.api
    },
    // Not sure about the name for this
    allProjects(state) {
      const settingsStore = useSettingsStore()

      let projects = state.projects
      if (!settingsStore.showHiddenPages) {
        projects = projects.filter((project) => project.name !== ALPHA_PROJECT_DETAIL_PAGES)
      }

      return [
        ...projects,
        ...(state.manageData ? [state.manageData] : [])
      ]
    }
  }
})
